import Field from "./Field";
import State from "./State";
import AuthorizationClient from "./AuthorizationClient";
import i18next from "i18next";

export type AuthorizationFormOptions = {
    loginField: string,
    passwordField: string,
    codeField: string,
    browserInfoField: string,
    buttonContainer: string,
    error: string,
    buttonCancel: string
};

export default class AuthorizationForm
{
    public element: JQuery;
    private loginField: Field;
    private passwordField: Field;
    private codeField: Field;
    private browserInfoField: Field;
    private buttonContainer: JQuery;
    private error: JQuery;
    private state: State;
    private buttonCancel: JQuery;
    constructor(element: any, options: AuthorizationFormOptions)
    {
        this.element = $(element);
        this.loginField = new Field(this, options.loginField, (value, field) => {
            if (value === "") {
                field.setError(i18next.t("authorization_form:provide_username"));
            } else {
                field.setSuccess();
            }
        });
        this.passwordField = new Field(this, options.passwordField, (value, field) => {
            if (value === "") {
                field.setError(i18next.t("authorization_form:provide_password"));
            } else {
                field.setSuccess();
            }
        });
        this.codeField = new Field(this, options.codeField, (value, field) => {
            if (
                value.length !== 6 ||
                !$.isNumeric(value)
            ) {
                field.setError(i18next.t("authorization_form:code_required"));
            } else {
                field.setSuccess();
            }
        });
        this.browserInfoField = new Field(this, options.browserInfoField, (value, field) => {
            field.setSuccess();
        });
        this.buttonContainer = $(options.buttonContainer, this.element);
        this.buttonCancel = $(options.buttonCancel, this.element);
        this.error = $(options.error, this.element);

        this.state = State.PARTICLE;
    }
    setState(newState: State)
    {
        if (this.state !== newState) {
            if (newState === State.PARTICLE) {
                this.loginField.setDisabled(false);
                this.loginField.focus();
                this.passwordField.setDisabled(false);
                this.codeField.setDisabled(true);
                this.codeField.reset();
                this.buttonContainer.removeClass("state-1");
                this.state = newState;
            } else if (newState === State.FULL) {
                this.loginField.setDisabled(true);
                this.passwordField.setDisabled(true);
                this.codeField.setDisabled(false);
                this.codeField.setReadOnly(false);
                this.codeField.focus();
                this.buttonContainer.addClass("state-1");
                this.state = newState;
            } else {
                throw "Undefined state";
            }
        }
    }
    async handle(): Promise<void> {
        if (this.state === State.PARTICLE) {
            // ajax call to login api endpoint, send credentials
            try {
                await AuthorizationClient.partialAuthorization(
                    this.loginField.getValue(),
                    this.passwordField.getValue()
                );
                this.loginField.setSuccess();
                this.passwordField.setSuccess();
                this.setSuccess();
                this.setState(State.FULL);
            } catch (e) {
                this.loginField.setError(i18next.t("authorization_form:provide_username"));
                this.passwordField.setError(i18next.t("authorization_form:provide_password"));
                if (typeof e.authErrorCode === "undefined") {
                    this.setError(i18next.t("authorization_form:invalid_credentials"));
                } else {
                    this.setError(i18next.t("authorization_form:invalid_credentials") + '. Code: ' + e.authErrorCode);
                }
            }
        } else if (this.state === State.FULL) {
            try {
                if(this.codeField.getValue() == "" || !this.codeField.isValid()) {
                    this.codeField.reset();
                    this.setError(i18next.t("authorization_form:invalid_code"));
                    return;
                }
                let response = await AuthorizationClient.authorization(
                    this.loginField.getValue(),
                    this.passwordField.getValue(),
                    this.codeField.getValue(),
                    this.browserInfoField.getValue()
                );
                this.codeField.setSuccess();
                this.setSuccess();
                window.location.href = response.targetUrl;
            } catch (e) {
                this.codeField.reset();
                if (typeof e.authErrorCode === "undefined") {
                    this.setError(i18next.t("authorization_form:invalid_code"));
                } else {
                    this.setError(i18next.t("authorization_form:invalid_code") + '. Code: ' + e.authErrorCode);
                }
            }
        } else {
            throw "Undefined state";
        }
    }

    initialize()
    {
        this.loginField.initialize();
        this.passwordField.initialize();
        this.codeField.initialize();
        this.element.on('submit', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                await this.handle();
            } catch (e) {}
        });
        this.buttonCancel.on('click', () => this.setState(State.PARTICLE));
    }

    setError(message: string)
    {
        this.error.html(message).show();
    }

    setSuccess()
    {
        this.error.hide().empty();
    }

    isValid()
    {
        if (this.state === State.PARTICLE) {
            if (
                this.loginField.isValid() &&
                this.passwordField.isValid()
            ) {
                this.setSuccess();
                return true;
            } else {
                this.setError(i18next.t("authorization_form:invalid_credentials"));
                return false;
            }
        } else if (this.state === State.FULL) {
            if ( this.codeField.isValid() ) {
                return true;
            } else {
                this.setError(i18next.t("authorization_form:invalid_code"));
                return false;
            }
        }
        throw "Undefined state";
    }
}
